import {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  PageContainer,
  type ComponentDiagnostic,
} from "@swot/base-components";
import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridRowAction,
} from "@components/data-grid/index.js";

type Row = Record<string, unknown>;
type SecurityTab = "events" | "access" | "blocked";
type ExportFormat = "CSV" | "XLS" | "JSON";

type ActionResult = {
  readonly success: boolean;
  readonly title: string;
  readonly message: string;
};

export type CmtsSecurityLogsPageProps = {
  readonly rows?: unknown;
  readonly accessDecisions?: unknown;
  readonly blockedActions?: unknown;
  readonly diagnostics?:
    | readonly ComponentDiagnostic[]
    | undefined;
  readonly loading?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly onRefresh?: (() => void | Promise<void>) | undefined;
  readonly onOpenSecurityEvent?:
    | ((row: Row) => void | Promise<Row | void>)
    | undefined;
  readonly onMarkReviewed?:
    | ((row: Row) => void | Promise<void>)
    | undefined;
  readonly onCreateAlert?:
    | ((row: Row) => void | Promise<void>)
    | undefined;
  readonly onExportSecurity?:
    | ((
        rows: readonly Row[],
        format: ExportFormat,
      ) => void | Promise<void>)
    | undefined;
};

const eventColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "occurredAt",
    header: "Occurred At",
    accessor: readOccurredAt,
    sortable: true,
    filterable: true,
    minWidth: 185,
  },
  {
    id: "eventType",
    header: "Security Event",
    accessor: readEventType,
    sortable: true,
    filterable: true,
    minWidth: 230,
  },
  {
    id: "risk",
    header: "Risk",
    accessor: readRisk,
    sortable: true,
    filterable: true,
    minWidth: 145,
  },
  {
    id: "actor",
    header: "Actor",
    accessor: readActor,
    sortable: true,
    filterable: true,
    minWidth: 180,
  },
  {
    id: "status",
    header: "Status",
    accessor: readStatus,
    sortable: true,
    filterable: true,
    minWidth: 165,
  },
  {
    id: "source",
    header: "Source",
    accessor: readSource,
    sortable: true,
    filterable: true,
    minWidth: 175,
  },
  {
    id: "correlation",
    header: "Correlation",
    accessor: readCorrelation,
    sortable: true,
    filterable: true,
    minWidth: 165,
  },
];

const accessColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "occurredAt",
    header: "Occurred At",
    accessor: readOccurredAt,
    sortable: true,
    filterable: true,
    minWidth: 185,
  },
  {
    id: "decision",
    header: "Decision",
    accessor: readAccessDecision,
    sortable: true,
    filterable: true,
    minWidth: 155,
  },
  {
    id: "resource",
    header: "Resource",
    accessor: readResource,
    sortable: true,
    filterable: true,
    minWidth: 210,
  },
  {
    id: "permission",
    header: "Permission",
    accessor: readPermission,
    sortable: true,
    filterable: true,
    minWidth: 205,
  },
  {
    id: "actor",
    header: "Actor",
    accessor: readActor,
    sortable: true,
    filterable: true,
    minWidth: 175,
  },
  {
    id: "reason",
    header: "Reason",
    accessor: readReason,
    sortable: true,
    filterable: true,
    minWidth: 285,
  },
  {
    id: "correlation",
    header: "Correlation",
    accessor: readCorrelation,
    sortable: true,
    filterable: true,
    minWidth: 165,
  },
];

const blockedColumns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "occurredAt",
    header: "Occurred At",
    accessor: readOccurredAt,
    sortable: true,
    filterable: true,
    minWidth: 185,
  },
  {
    id: "action",
    header: "Blocked Action",
    accessor: readBlockedAction,
    sortable: true,
    filterable: true,
    minWidth: 225,
  },
  {
    id: "risk",
    header: "Risk",
    accessor: readRisk,
    sortable: true,
    filterable: true,
    minWidth: 145,
  },
  {
    id: "actor",
    header: "Actor",
    accessor: readActor,
    sortable: true,
    filterable: true,
    minWidth: 175,
  },
  {
    id: "status",
    header: "Status",
    accessor: readStatus,
    sortable: true,
    filterable: true,
    minWidth: 165,
  },
  {
    id: "reason",
    header: "Block Reason",
    accessor: readReason,
    sortable: true,
    filterable: true,
    minWidth: 300,
  },
  {
    id: "correlation",
    header: "Correlation",
    accessor: readCorrelation,
    sortable: true,
    filterable: true,
    minWidth: 165,
  },
];

export function CmtsSecurityLogsPage(
  props: CmtsSecurityLogsPageProps,
) {
  const [activeTab, setActiveTab] =
    useState<SecurityTab>("events");
  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [actorFilter, setActorFilter] = useState("ALL");

  const [actionRow, setActionRow] = useState<Row | null>(
    null,
  );
  const [detailRow, setDetailRow] = useState<Row | null>(
    null,
  );
  const [correlationRow, setCorrelationRow] =
    useState<Row | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] =
    useState<ExportFormat>("CSV");
  const [busyAction, setBusyAction] = useState<
    string | null
  >(null);
  const [actionResult, setActionResult] =
    useState<ActionResult | null>(null);

  const events = useMemo(
    () => normalizeRows(props.rows, "security-event"),
    [props.rows],
  );

  const accessDecisions = useMemo(
    () =>
      normalizeRows(
        props.accessDecisions,
        "access-decision",
      ),
    [props.accessDecisions],
  );

  const blockedActions = useMemo(
    () =>
      normalizeRows(
        props.blockedActions,
        "blocked-action",
      ),
    [props.blockedActions],
  );

  const activeRows =
    activeTab === "events"
      ? events
      : activeTab === "access"
        ? accessDecisions
        : blockedActions;

  const activeColumns =
    activeTab === "events"
      ? eventColumns
      : activeTab === "access"
        ? accessColumns
        : blockedColumns;

  const visibleRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return activeRows.filter((row) => {
      const text = [
        readEventType(row),
        readBlockedAction(row),
        readResource(row),
        readPermission(row),
        readRiskRaw(row),
        readStatusRaw(row),
        readActor(row),
        readReason(row),
        readSource(row),
      ]
        .join(" ")
        .toLowerCase();

      if (
        normalizedSearch &&
        !text.includes(normalizedSearch)
      ) {
        return false;
      }

      if (
        riskFilter !== "ALL" &&
        readRiskRaw(row) !== riskFilter
      ) {
        return false;
      }

      if (
        statusFilter !== "ALL" &&
        readStatusRaw(row) !== statusFilter
      ) {
        return false;
      }

      if (
        actorFilter !== "ALL" &&
        readActor(row) !== actorFilter
      ) {
        return false;
      }

      return true;
    });
  }, [
    activeRows,
    search,
    riskFilter,
    statusFilter,
    actorFilter,
  ]);

  const highRiskCount = [...events, ...blockedActions].filter(
    (row) =>
      ["HIGH", "CRITICAL", "SEVERE"].includes(
        readRiskRaw(row),
      ),
  ).length;

  const deniedCount = accessDecisions.filter((row) =>
    ["DENIED", "BLOCKED", "REJECTED"].includes(
      readAccessDecisionRaw(row),
    ),
  ).length;

  const reviewedCount = [
    ...events,
    ...blockedActions,
  ].filter((row) =>
    ["REVIEWED", "RESOLVED", "CLOSED"].includes(
      readStatusRaw(row),
    ),
  ).length;

  const risks = uniqueValues(activeRows.map(readRiskRaw));
  const statuses = uniqueValues(
    activeRows.map(readStatusRaw),
  );
  const actors = uniqueValues(activeRows.map(readActor));

  const correlationRows = useMemo(() => {
    const correlationId = readRawCorrelation(correlationRow);

    if (!correlationId) {
      return [];
    }

    return [
      ...events,
      ...accessDecisions,
      ...blockedActions,
    ].filter(
      (row) => readRawCorrelation(row) === correlationId,
    );
  }, [
    correlationRow,
    events,
    accessDecisions,
    blockedActions,
  ]);

  const gridActions = useMemo<
    readonly CmtsGridRowAction<Row>[]
  >(
    () => [
      {
        id: "security-actions",
        label: "⋮ Actions",
        onClick: (row) => {
          setActionRow(row);
        },
      },
    ],
    [],
  );

  function changeTab(tab: SecurityTab) {
    setActiveTab(tab);
    setSearch("");
    setRiskFilter("ALL");
    setStatusFilter("ALL");
    setActorFilter("ALL");
    setActionRow(null);
  }

  const openDetails = useCallback((row: Row) => {
    setActionRow(null);
    setDetailRow(row);

    if (!props.onOpenSecurityEvent) {
      return;
    }

    void Promise.resolve(props.onOpenSecurityEvent(row))
      .then((response) => {
        if (!response) {
          return;
        }

        setDetailRow((current) => {
          if (!current || readRowId(current) !== readRowId(row)) {
            return current;
          }

          return { ...row, ...response };
        });
      })
      .catch(() => {
        // The grid row is already visible in the detail modal.
      });
  }, [props.onOpenSecurityEvent]);

  async function performAction(
    name: string,
    row: Row,
    handler: ((row: Row) => void | Promise<void>) | undefined,
    successTitle: string,
    successMessage: string,
  ) {
    if (!handler) {
      return;
    }

    setActionRow(null);
    setBusyAction(name);

    try {
      await handler(row);

      setActionResult({
        success: true,
        title: successTitle,
        message: successMessage,
      });
    } catch (error) {
      setActionResult({
        success: false,
        title: `${successTitle} failed`,
        message: getErrorMessage(error),
      });
    } finally {
      setBusyAction(null);
    }
  }

  async function exportRows() {
    if (!visibleRows.length) {
      return;
    }

    setBusyAction("export");

    try {
      await props.onExportSecurity?.(
        visibleRows,
        exportFormat,
      );

      downloadSecurityRows(
        activeTab,
        visibleRows,
        exportFormat,
      );

      setExportOpen(false);
    } catch (error) {
      setExportOpen(false);

      setActionResult({
        success: false,
        title: "Security export failed",
        message: getErrorMessage(error),
      });
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div className="cmts-security-blue">
      <PageContainer
        title="Security Operations"
        subtitle="Security events, access decisions, blocked actions, risk review and governed alert controls."
      >
        <section className="cmts-security-hero">
          <div>
            <span className="cmts-security-eyebrow">
              AUDIT &amp; SECURITY
            </span>

            <h2>Security operations center</h2>

            <p>
              Review security activity, access decisions, blocked
              operations and correlation chains from one governed
              operational workspace.
            </p>
          </div>

          <div className="cmts-security-hero__actions">
            <button
              type="button"
              className="cmts-security-secondary-button"
              disabled={!props.onRefresh || props.loading}
              onClick={() => void props.onRefresh?.()}
            >
              ↻ Refresh data
            </button>

            <button
              type="button"
              className="cmts-security-primary-button"
              disabled={visibleRows.length === 0}
              onClick={() => setExportOpen(true)}
            >
              ⇩ Export current view
            </button>
          </div>
        </section>

        <section className="cmts-security-summary-grid">
          <SummaryCard
            label="Security events"
            value={events.length}
            description="Recorded security activity"
            tone="blue"
            icon="◉"
          />

          <SummaryCard
            label="High risk"
            value={highRiskCount}
            description="High or critical risk records"
            tone="red"
            icon="!"
          />

          <SummaryCard
            label="Access denied"
            value={deniedCount}
            description="Denied access decisions"
            tone="orange"
            icon="×"
          />

          <SummaryCard
            label="Blocked actions"
            value={blockedActions.length}
            description="Prevented system actions"
            tone="purple"
            icon="⊘"
          />

          <SummaryCard
            label="Reviewed"
            value={reviewedCount}
            description="Completed security reviews"
            tone="green"
            icon="✓"
          />
        </section>

        <section className="cmts-security-policy">
          <span>◆</span>

          <div>
            <strong>Security review policy</strong>
            <p>
              Security events and blocked actions are
              repository-backed. Review and alert actions persist
              audit records and refresh the affected security
              collections.
            </p>
          </div>
        </section>

        <section className="cmts-security-workspace">
          <nav className="cmts-security-tabs">
            <TabButton
              label="Security Events"
              count={events.length}
              active={activeTab === "events"}
              onClick={() => changeTab("events")}
            />

            <TabButton
              label="Access Decisions"
              count={accessDecisions.length}
              active={activeTab === "access"}
              onClick={() => changeTab("access")}
            />

            <TabButton
              label="Blocked Actions"
              count={blockedActions.length}
              active={activeTab === "blocked"}
              onClick={() => changeTab("blocked")}
            />
          </nav>

          <header className="cmts-security-workspace__header">
            <div>
              <h3>{getTabTitle(activeTab)}</h3>

              <p>
                Showing {visibleRows.length} of {activeRows.length}{" "}
                records.
              </p>
            </div>

            <div className="cmts-security-workspace__actions">
              <button
                type="button"
                className="cmts-security-secondary-button"
                onClick={() => {
                  setSearch("");
                  setRiskFilter("ALL");
                  setStatusFilter("ALL");
                  setActorFilter("ALL");
                }}
              >
                Clear filters
              </button>

              <button
                type="button"
                className="cmts-security-primary-button"
                disabled={visibleRows.length === 0}
                onClick={() => setExportOpen(true)}
              >
                ⇩ Export
              </button>
            </div>
          </header>

          <div className="cmts-security-filters">
            <label className="cmts-security-filter cmts-security-filter--search">
              <span>Search</span>

              <input
                type="search"
                value={search}
                placeholder="Search event, actor, resource..."
                onChange={(event) =>
                  setSearch(event.target.value)
                }
              />
            </label>

            <FilterSelect
              label="Risk"
              value={riskFilter}
              options={risks}
              allLabel="All risks"
              onChange={setRiskFilter}
            />

            <FilterSelect
              label="Status"
              value={statusFilter}
              options={statuses}
              allLabel="All statuses"
              onChange={setStatusFilter}
            />

            <FilterSelect
              label="Actor"
              value={actorFilter}
              options={actors}
              allLabel="All actors"
              onChange={setActorFilter}
            />
          </div>

          {props.error ? (
            <div className="cmts-security-error">
              <strong>
                Some security data could not be loaded
              </strong>
              <span>{props.error}</span>
            </div>
          ) : null}

          <div className="cmts-security-grid">
            <CmtsSmartDataGrid<Row>
              title={`${visibleRows.length} ${getTabTitle(
                activeTab,
              ).toLowerCase()} records`}
              rows={visibleRows}
              columns={activeColumns}
              rowKey="id"
              defaultPageSize={10}
              pageSizeOptions={[10, 25, 50]}
              sortable
              searchable={false}
              filterable={false}
              loading={props.loading}
              actions={gridActions}
              emptyMessage={getEmptyMessage(activeTab)}
            />
          </div>
        </section>
      </PageContainer>

      {actionRow ? (
        <SecurityActionsModal
          tab={activeTab}
          row={actionRow}
          busy={Boolean(busyAction)}
          canReview={Boolean(props.onMarkReviewed)}
          canAlert={Boolean(props.onCreateAlert)}
          onClose={() => setActionRow(null)}
          onDetail={() => void openDetails(actionRow)}
          onCorrelation={() => {
            setActionRow(null);
            setCorrelationRow(actionRow);
          }}
          onReview={() =>
            void performAction(
              "review",
              actionRow,
              props.onMarkReviewed,
              "Security event reviewed",
              `${readEventType(
                actionRow,
              )} was marked as reviewed.`,
            )
          }
          onAlert={() =>
            void performAction(
              "alert",
              actionRow,
              props.onCreateAlert,
              "Security alert created",
              `A security alert was created for ${readEventType(
                actionRow,
              )}.`,
            )
          }
        />
      ) : null}

      {detailRow ? (
        <SecurityDetailModal
          tab={activeTab}
          row={detailRow}
          onClose={() => setDetailRow(null)}
        />
      ) : null}

      {correlationRow ? (
        <CorrelationModal
          rows={correlationRows}
          onClose={() => setCorrelationRow(null)}
          onOpen={(row) => {
            setCorrelationRow(null);
            setDetailRow(row);
          }}
        />
      ) : null}

      {actionResult ? (
        <ActionResultModal
          result={actionResult}
          onClose={() => setActionResult(null)}
        />
      ) : null}

      {exportOpen ? (
        <ExportModal
          title={getTabTitle(activeTab)}
          recordCount={visibleRows.length}
          format={exportFormat}
          exporting={busyAction === "export"}
          onFormatChange={setExportFormat}
          onClose={() => setExportOpen(false)}
          onExport={() => void exportRows()}
        />
      ) : null}
    </div>
  );
}

function SummaryCard(props: {
  readonly label: string;
  readonly value: number;
  readonly description: string;
  readonly tone: string;
  readonly icon: string;
}) {
  return (
    <article
      className={`cmts-security-summary-card cmts-security-summary-card--${props.tone}`}
    >
      <div>
        <span>{props.label}</span>
        <i>{props.icon}</i>
      </div>

      <strong>{props.value}</strong>
      <p>{props.description}</p>
    </article>
  );
}

function TabButton(props: {
  readonly label: string;
  readonly count: number;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`cmts-security-tab${
        props.active ? " cmts-security-tab--active" : ""
      }`}
      onClick={props.onClick}
    >
      <span>{props.label}</span>
      <strong>{props.count}</strong>
    </button>
  );
}

function FilterSelect(props: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly string[];
  readonly allLabel: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="cmts-security-filter">
      <span>{props.label}</span>

      <select
        value={props.value}
        onChange={(event) =>
          props.onChange(event.target.value)
        }
      >
        <option value="ALL">{props.allLabel}</option>

        {props.options.map((option) => (
          <option key={option} value={option}>
            {humanize(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function SecurityActionsModal(props: {
  readonly tab: SecurityTab;
  readonly row: Row;
  readonly busy: boolean;
  readonly canReview: boolean;
  readonly canAlert: boolean;
  readonly onClose: () => void;
  readonly onDetail: () => void;
  readonly onCorrelation: () => void;
  readonly onReview: () => void;
  readonly onAlert: () => void;
}) {
  return (
    <ModalShell
      title="Security actions"
      subtitle={readEventType(props.row)}
      onClose={props.onClose}
    >
      <div className="cmts-audit-action-list">
        <ActionOption
          icon="◉"
          title="View security details"
          description="Review readable event, risk, actor and decision information."
          onClick={props.onDetail}
        />

        {props.tab !== "access" ? (
          <>
            <ActionOption
              icon="✓"
              title="Mark reviewed"
              description="Mark this security record as reviewed."
              disabled={
                !props.canReview ||
                props.busy ||
                readStatusRaw(props.row) === "REVIEWED"
              }
              onClick={props.onReview}
            />

            <ActionOption
              icon="!"
              title="Create security alert"
              description="Create an alert linked to this security event."
              disabled={!props.canAlert || props.busy}
              onClick={props.onAlert}
            />
          </>
        ) : null}

        <ActionOption
          icon="⛓"
          title="View correlation chain"
          description="Review related security events and access decisions."
          disabled={!readRawCorrelation(props.row)}
          onClick={props.onCorrelation}
        />
      </div>
    </ModalShell>
  );
}

function ActionOption(props: {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="cmts-audit-action-option"
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <span className="cmts-audit-action-option__icon">
        {props.icon}
      </span>

      <span>
        <strong>{props.title}</strong>
        <small>{props.description}</small>
      </span>

      <span className="cmts-audit-action-option__arrow">
        ›
      </span>
    </button>
  );
}

function SecurityDetailModal(props: {
  readonly tab: SecurityTab;
  readonly row: Row;
  readonly onClose: () => void;
}) {
  const items = getDetailItems(props.tab, props.row);

  return (
    <ModalShell
      title={`${getTabTitle(props.tab)} details`}
      subtitle={readEventType(props.row)}
      onClose={props.onClose}
      wide
    >
      <div className="cmts-security-detail-banner">
        <div>
          <span>{readSource(props.row)}</span>
          <strong>{readEventType(props.row)}</strong>
        </div>

        <span className="cmts-security-detail-risk">
          {readRisk(props.row)}
        </span>
      </div>

      <div className="cmts-security-detail-grid">
        {items.map(([label, value]) => (
          <div
            key={label}
            className="cmts-security-detail-item"
          >
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

function CorrelationModal(props: {
  readonly rows: readonly Row[];
  readonly onClose: () => void;
  readonly onOpen: (row: Row) => void;
}) {
  return (
    <ModalShell
      title="Security correlation chain"
      subtitle={`${props.rows.length} related records`}
      onClose={props.onClose}
      wide
    >
      {props.rows.length ? (
        <div className="cmts-security-correlation-list">
          {props.rows.map((row, index) => (
            <button
              type="button"
              key={String(row.id ?? index)}
              className="cmts-security-correlation-row"
              onClick={() => props.onOpen(row)}
            >
              <span>{index + 1}</span>

              <div>
                <strong>{readEventType(row)}</strong>
                <small>
                  {readActor(row)} • {readStatus(row)}
                </small>
              </div>

              <em>{readOccurredAt(row)}</em>
            </button>
          ))}
        </div>
      ) : (
        <div className="cmts-security-empty">
          No related security records were found.
        </div>
      )}

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Done
        </button>
      </div>
    </ModalShell>
  );
}

function ActionResultModal(props: {
  readonly result: ActionResult;
  readonly onClose: () => void;
}) {
  return (
    <ModalShell
      title={props.result.title}
      onClose={props.onClose}
    >
      <div
        className={`cmts-security-action-result ${
          props.result.success
            ? "cmts-security-action-result--success"
            : "cmts-security-action-result--failure"
        }`}
      >
        <span>{props.result.success ? "✓" : "!"}</span>

        <div>
          <strong>
            {props.result.success
              ? "Operation completed"
              : "Operation failed"}
          </strong>
          <p>{props.result.message}</p>
        </div>
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-primary-button"
          onClick={props.onClose}
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
}

function ExportModal(props: {
  readonly title: string;
  readonly recordCount: number;
  readonly format: ExportFormat;
  readonly exporting: boolean;
  readonly onFormatChange: (format: ExportFormat) => void;
  readonly onClose: () => void;
  readonly onExport: () => void;
}) {
  const formats: readonly ExportFormat[] = [
    "CSV",
    "XLS",
    "JSON",
  ];

  return (
    <ModalShell
      title={`Export ${props.title}`}
      subtitle="Download the currently filtered security records."
      onClose={props.onClose}
      wide
    >
      <div className="cmts-export-banner">
        <span className="cmts-export-banner__icon">⇩</span>

        <div>
          <small>SECURITY DATA EXPORT</small>
          <strong>Prepare your export</strong>
          <p>
            Export readable security values without raw IDs.
          </p>
        </div>

        <span className="cmts-export-record-count">
          {props.recordCount} records
        </span>
      </div>

      <div className="cmts-export-format-grid">
        {formats.map((format) => {
          const selected = format === props.format;

          return (
            <button
              type="button"
              key={format}
              className={`cmts-export-format-card${
                selected
                  ? " cmts-export-format-card--selected"
                  : ""
              }`}
              onClick={() =>
                props.onFormatChange(format)
              }
            >
              <span className="cmts-export-format-badge">
                {format}
              </span>

              <span
                className={`cmts-export-radio${
                  selected
                    ? " cmts-export-radio--selected"
                    : ""
                }`}
              >
                {selected ? "✓" : ""}
              </span>

              <strong>
                {format === "CSV"
                  ? "CSV spreadsheet"
                  : format === "XLS"
                    ? "Excel workbook"
                    : "JSON data"}
              </strong>

              <small>
                Download filtered security records.
              </small>
            </button>
          );
        })}
      </div>

      <div className="cmts-audit-modal-footer">
        <button
          type="button"
          className="cmts-audit-secondary-button"
          disabled={props.exporting}
          onClick={props.onClose}
        >
          Cancel
        </button>

        <button
          type="button"
          className="cmts-audit-primary-button"
          disabled={props.exporting}
          onClick={props.onExport}
        >
          {props.exporting
            ? "Preparing..."
            : `Download ${props.format}`}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell(props: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
  readonly wide?: boolean;
}) {
  return (
    <div
      className="cmts-audit-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          props.onClose();
        }
      }}
    >
      <section
        className={`cmts-audit-modal${
          props.wide ? " cmts-audit-modal--wide" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
      >
        <header className="cmts-audit-modal__header">
          <div>
            <h3>{props.title}</h3>
            {props.subtitle ? <p>{props.subtitle}</p> : null}
          </div>

          <button
            type="button"
            className="cmts-audit-modal__close"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>

        <div className="cmts-audit-modal__body">
          {props.children}
        </div>
      </section>
    </div>
  );
}

function readRowId(row: Row): string {
  return String(
    row.id ??
      row.securityEventId ??
      row.accessDecisionId ??
      row.blockedActionId ??
      row.code ??
      "",
  );
}

function normalizeRows(
  value: unknown,
  prefix: string,
): readonly Row[] {
  return extractRows(value).map((row, index) => ({
    ...row,
    id:
      row.id ??
      row.securityEventId ??
      row.accessDecisionId ??
      row.blockedActionId ??
      row.code ??
      `${prefix}-${index + 1}`,
  }));
}

function extractRows(
  value: unknown,
  depth = 0,
): readonly Row[] {
  if (depth > 6) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(isRecord);
  }

  if (!isRecord(value)) {
    return [];
  }

  if (
    value.success === false ||
    value.ok === false ||
    value.isSuccess === false
  ) {
    return [];
  }

  const keys = [
    "data",
    "items",
    "rows",
    "records",
    "events",
    "securityEvents",
    "accessDecisions",
    "blockedActions",
    "values",
    "result",
    "payload",
  ] as const;

  for (const key of keys) {
    const candidate = value[key];

    if (Array.isArray(candidate)) {
      return candidate.filter(isRecord);
    }

    if (isRecord(candidate)) {
      const nested = extractRows(candidate, depth + 1);

      if (nested.length) {
        return nested;
      }
    }
  }

  return value.id || value.securityEventId
    ? [value]
    : [];
}

function getDetailItems(
  tab: SecurityTab,
  row: Row,
): readonly (readonly [string, string])[] {
  if (tab === "access") {
    return [
      ["Occurred At", readOccurredAt(row)],
      ["Decision", readAccessDecision(row)],
      ["Resource", readResource(row)],
      ["Permission", readPermission(row)],
      ["Actor", readActor(row)],
      ["Reason", readReason(row)],
      ["Correlation", readCorrelation(row)],
    ];
  }

  if (tab === "blocked") {
    return [
      ["Occurred At", readOccurredAt(row)],
      ["Blocked Action", readBlockedAction(row)],
      ["Risk", readRisk(row)],
      ["Actor", readActor(row)],
      ["Status", readStatus(row)],
      ["Block Reason", readReason(row)],
      ["Correlation", readCorrelation(row)],
    ];
  }

  return [
    ["Occurred At", readOccurredAt(row)],
    ["Security Event", readEventType(row)],
    ["Risk", readRisk(row)],
    ["Actor", readActor(row)],
    ["Status", readStatus(row)],
    ["Source", readSource(row)],
    ["Reason", readReason(row)],
    ["Correlation", readCorrelation(row)],
  ];
}

function readOccurredAt(row: Row): string {
  return formatDateTime(
    row.occurredAt ??
      row.createdAt ??
      row.eventAt ??
      row.timestamp ??
      row.decidedAt,
  );
}

function readEventType(row: Row): string {
  return humanize(
    row.eventName ??
      row.eventType ??
      row.actionName ??
      row.action ??
      row.code,
    "Security Event",
  );
}

function readBlockedAction(row: Row): string {
  return humanize(
    row.blockedActionName ??
      row.actionName ??
      row.action ??
      row.eventType,
    "Blocked Operation",
  );
}

function readRiskRaw(row: Row): string {
  return String(
    row.riskName ??
      row.riskLevel ??
      row.risk ??
      row.severity ??
      "MEDIUM",
  )
    .trim()
    .toUpperCase();
}

function readRisk(row: Row): string {
  const risk = readRiskRaw(row);

  if (["CRITICAL", "HIGH", "SEVERE"].includes(risk)) {
    return `🔴 ${humanize(risk)}`;
  }

  if (risk === "MEDIUM") {
    return "🟠 Medium";
  }

  return `🟢 ${humanize(risk)}`;
}

function readActor(row: Row): string {
  const value = String(
    row.actorName ??
      row.actorDisplayName ??
      row.actorUserName ??
      row.userName ??
      row.subjectName ??
      row.actorId ??
      "",
  ).trim();

  if (!value) {
    return "System";
  }

  return isUuid(value) ? "System user" : humanize(value);
}

function readStatusRaw(row: Row): string {
  return String(
    row.statusName ??
      row.status ??
      row.reviewStatus ??
      "OPEN",
  )
    .trim()
    .toUpperCase();
}

function readStatus(row: Row): string {
  const status = readStatusRaw(row);

  if (
    ["REVIEWED", "RESOLVED", "CLOSED", "ALLOWED"].includes(
      status,
    )
  ) {
    return `🟢 ${humanize(status)}`;
  }

  if (
    ["DENIED", "BLOCKED", "FAILED", "REJECTED"].includes(
      status,
    )
  ) {
    return `🔴 ${humanize(status)}`;
  }

  if (["PENDING", "OPEN", "NEW"].includes(status)) {
    return `🟠 ${humanize(status)}`;
  }

  return `🔵 ${humanize(status)}`;
}

function readAccessDecisionRaw(row: Row): string {
  return String(
    row.decisionName ??
      row.decision ??
      row.accessDecision ??
      row.result ??
      row.status ??
      "NOT_RECORDED",
  )
    .trim()
    .toUpperCase();
}

function readAccessDecision(row: Row): string {
  const decision = readAccessDecisionRaw(row);

  if (["ALLOWED", "GRANTED", "PERMITTED"].includes(decision)) {
    return `🟢 ${humanize(decision)}`;
  }

  if (["DENIED", "BLOCKED", "REJECTED"].includes(decision)) {
    return `🔴 ${humanize(decision)}`;
  }

  return `🟠 ${humanize(decision)}`;
}

function readResource(row: Row): string {
  return humanize(
    row.resourceName ??
      row.resourceType ??
      row.entityName ??
      row.entityType ??
      row.moduleName ??
      row.module,
    "System Resource",
  );
}

function readPermission(row: Row): string {
  const value = String(
    row.permissionName ??
      row.permissionCode ??
      row.requiredPermissionCode ??
      row.permission ??
      "",
  ).trim();

  return !value || isUuid(value)
    ? "No permission recorded"
    : value;
}

function readReason(row: Row): string {
  const value = String(
    row.reason ??
      row.failureReason ??
      row.blockReason ??
      row.decisionReason ??
      row.summary ??
      row.message ??
      "",
  ).trim();

  return value || "No additional reason recorded";
}

function readSource(row: Row): string {
  return humanize(
    row.sourceName ??
      row.source ??
      row.moduleName ??
      row.module ??
      row.origin,
    "CMTS",
  );
}

function readRawCorrelation(row: Row | null): string {
  if (!row) {
    return "";
  }

  return String(
    row.correlationId ??
      row.requestId ??
      row.traceId ??
      "",
  ).trim();
}

function readCorrelation(row: Row): string {
  return readRawCorrelation(row)
    ? "🔗 Chain available"
    : "No correlation";
}

function getTabTitle(tab: SecurityTab): string {
  return tab === "events"
    ? "Security Events"
    : tab === "access"
      ? "Access Decisions"
      : "Blocked Actions";
}

function getEmptyMessage(tab: SecurityTab): string {
  return tab === "events"
    ? "No backend security events were returned for this scope."
    : tab === "access"
      ? "No access decisions were returned for this scope."
      : "No blocked security actions were returned for this scope.";
}

function uniqueValues(
  values: readonly string[],
): readonly string[] {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
}

function humanize(
  value: unknown,
  fallback = "Not available",
): string {
  const text = String(value ?? "").trim();

  if (!text || isUuid(text)) {
    return fallback;
  }

  return text
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (character) =>
      character.toUpperCase(),
    );
}

function formatDateTime(value: unknown): string {
  const text = String(value ?? "").trim();

  if (!text) {
    return "Not recorded";
  }

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) {
    return humanize(text, "Not recorded");
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isRecord(value: unknown): value is Row {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The security operation could not be completed.";
}

function downloadSecurityRows(
  tab: SecurityTab,
  rows: readonly Row[],
  format: ExportFormat,
) {
  const exportRows = rows.map((row) => {
    if (tab === "access") {
      return {
        "Occurred At": readOccurredAt(row),
        Decision: readAccessDecision(row).replace(
          /^[^\s]+\s/,
          "",
        ),
        Resource: readResource(row),
        Permission: readPermission(row),
        Actor: readActor(row),
        Reason: readReason(row),
        Correlation: readCorrelation(row).replace(
          /^[^\s]+\s/,
          "",
        ),
      };
    }

    if (tab === "blocked") {
      return {
        "Occurred At": readOccurredAt(row),
        "Blocked Action": readBlockedAction(row),
        Risk: readRisk(row).replace(/^[^\s]+\s/, ""),
        Actor: readActor(row),
        Status: readStatus(row).replace(/^[^\s]+\s/, ""),
        Reason: readReason(row),
        Correlation: readCorrelation(row).replace(
          /^[^\s]+\s/,
          "",
        ),
      };
    }

    return {
      "Occurred At": readOccurredAt(row),
      "Security Event": readEventType(row),
      Risk: readRisk(row).replace(/^[^\s]+\s/, ""),
      Actor: readActor(row),
      Status: readStatus(row).replace(/^[^\s]+\s/, ""),
      Source: readSource(row),
      Correlation: readCorrelation(row).replace(
        /^[^\s]+\s/,
        "",
      ),
    };
  });

  exportRecords(
    exportRows,
    format,
    `security-${tab}`,
  );
}

function exportRecords(
  rows: readonly Row[],
  format: ExportFormat,
  prefix: string,
) {
  const timestamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[:T]/g, "-");
  const headers = Object.keys(rows[0] ?? {});

  if (format === "JSON") {
    saveBlob(
      JSON.stringify(rows, null, 2),
      `${prefix}-${timestamp}.json`,
      "application/json;charset=utf-8",
    );
    return;
  }

  if (format === "XLS") {
    const html = `<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${headers
      .map((header) => `<th>${escapeHtml(header)}</th>`)
      .join("")}</tr></thead><tbody>${rows
      .map(
        (row) =>
          `<tr>${headers
            .map(
              (header) =>
                `<td>${escapeHtml(
                  String(row[header] ?? ""),
                )}</td>`,
            )
            .join("")}</tr>`,
      )
      .join("")}</tbody></table></body></html>`;

    saveBlob(
      html,
      `${prefix}-${timestamp}.xls`,
      "application/vnd.ms-excel;charset=utf-8",
    );
    return;
  }

  const csv = [
    headers.map(csvCell).join(","),
    ...rows.map((row) =>
      headers
        .map((header) => csvCell(row[header]))
        .join(","),
    ),
  ].join("\n");

  saveBlob(
    `\uFEFF${csv}`,
    `${prefix}-${timestamp}.csv`,
    "text/csv;charset=utf-8",
  );
}

function csvCell(value: unknown): string {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function saveBlob(
  content: string,
  fileName: string,
  mimeType: string,
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}